import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ChevronRight, ChevronDown } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { SectionHeading } from "../motion/SectionHeading";
import { Stagger, StaggerItem } from "../motion/Reveal";

// 4-step process. Each card reveals its description on hover or click.
export const Process = () => {
  const { t } = useLang();
  const steps = t.process.steps || [];
  const [active, setActive] = useState(null);

  return (
    <section className="py-10 sm:py-14 bg-charcoal text-white relative overflow-hidden grain" data-testid="process-section">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 relative">
        <SectionHeading overline={t.process.overline} title={t.process.title} sub={t.process.sub} light />

        <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-0 mt-6 sm:mt-10" stagger={0.1}>
          {steps.map((step, i) => {
            const open = active === i;
            return (
              <StaggerItem key={i}>
                <div
                  onMouseEnter={() => setActive(i)}
                  onMouseLeave={() => setActive(null)}
                  onClick={() => setActive(open ? null : i)}
                  data-testid={`process-step-${i}`}
                  className={`group relative h-full border p-4 sm:p-7 cursor-pointer transition-colors duration-500 ${
                    open ? "bg-green border-green" : "bg-white/[0.04] border-white/10 hover:border-gold/40"
                  }`}
                >
                  <div className="flex items-start justify-between mb-4 sm:mb-6">
                    <span className={`font-display text-3xl sm:text-4xl leading-none transition-colors ${open ? "text-gold" : "chapter-number"}`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all duration-500 ${open ? "bg-gold text-charcoal rotate-45" : "bg-white/10 text-gold"}`}>
                      <Plus className="w-4 h-4" />
                    </span>
                  </div>

                  <h3 className="font-display text-base sm:text-xl text-white leading-snug">{step.title}</h3>
                  <p className="text-gold text-[11px] sm:text-sm mt-1.5">{step.subtitle}</p>

                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="text-white/85 text-sm leading-relaxed mt-4 pt-4 border-t border-white/15" data-testid={`process-desc-${i}`}>
                          {step.desc}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Flow arrows: 01 -> 02 -> 03 -> 04 */}
                  {i < steps.length - 1 && (
                    <span
                      aria-hidden="true"
                      data-testid={`process-arrow-${i}`}
                      className={`absolute top-1/2 end-0 -translate-y-1/2 translate-x-1/2 rtl:-translate-x-1/2 z-20 pointer-events-none ${
                        i % 2 === 1 ? "hidden lg:block" : "block"
                      }`}
                    >
                      <motion.span
                        animate={{ x: [0, 6, 0] }}
                        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                        className="flex w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gold text-charcoal ring-4 ring-charcoal items-center justify-center shadow-[0_6px_18px_rgba(0,0,0,0.45)]"
                      >
                        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 rtl:rotate-180" strokeWidth={2.5} />
                      </motion.span>
                    </span>
                  )}

                  {i % 2 === 1 && i < steps.length - 1 && (
                    <span
                      aria-hidden="true"
                      data-testid={`process-arrow-down-${i}`}
                      className="lg:hidden absolute bottom-0 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 translate-y-1/2 z-20 pointer-events-none"
                    >
                      <motion.span
                        animate={{ y: [0, 6, 0] }}
                        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                        className="flex w-8 h-8 rounded-full bg-gold text-charcoal ring-4 ring-charcoal items-center justify-center shadow-[0_6px_18px_rgba(0,0,0,0.45)]"
                      >
                        <ChevronDown className="w-4 h-4" strokeWidth={2.5} />
                      </motion.span>
                    </span>
                  )}
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </div>
    </section>
  );
};
